import React, { useMemo } from "react";
import { Button, ConfigProvider, Table } from "antd";
import CreateRoleModal from "../components/CreateRoles/CreateRoleModal";
import CreateUserModal from "../components/CreateRoles/CreateUserModal";
import RevokeUserConfirmModal from "../components/CreateRoles/RevokeUserConfirmModal";
import { useCreateRolesPage } from "../hooks/useCreateRolesPage";
import {
  getCreateRolesRoleColumns,
  getCreateRolesUserColumns,
} from "../utils/createRolesTableColumns";
import "../styles/Pages/create-roles.css";

const CreateRoles = () => {
  const {
    lang,
    t,
    tabs,
    activeTab,
    setActiveTab,
    roles,
    assignUsers,
    roleModalOpen,
    closeRoleModal,
    openCreateRole,
    openEditRole,
    editingRole,
    modalPermissionRows,
    modalModuleAccessTypes,
    modalModulesLoading,
    modalModulesLoadError,
    userModalOpen,
    editingUser,
    userModalInitialRoleId,
    openUserModalForCreate,
    openUserModalForEdit,
    closeUserModal,
    rolesLoading,
    usersLoading,
    handleSaveRole,
    handleSaveUser,
    handleRevokeUser,
    revokingUserId,
    revokeTarget,
    revokeModalMode,
    closeRevokeModal,
    confirmRevokeUser,
    roleSelectOptions,
    moduleCatalog,
    moduleAccessTypesCatalog,
    marketRoleNameByKey,
    rolesTablePagination,
    usersTablePagination,
  } = useCreateRolesPage();

  const columns = useMemo(
    () =>
      getCreateRolesRoleColumns({
        t,
        lang,
        moduleCatalog,
        moduleAccessTypesCatalog,
        onEditRole: openEditRole,
      }),
    [t, lang, moduleCatalog, moduleAccessTypesCatalog, openEditRole],
  );

  const userColumns = useMemo(
    () =>
      getCreateRolesUserColumns({
        t,
        lang,
        marketRoleNameByKey,
        onEditUser: openUserModalForEdit,
        onRevokeUser: handleRevokeUser,
        revokingUserId,
      }),
    [
      t,
      lang,
      marketRoleNameByKey,
      openUserModalForEdit,
      handleRevokeUser,
      revokingUserId,
    ],
  );

  return (
    <div className="create-roles-page p-24">
      <h1 className="text-24 mb-8 text-3F3F3F">{t.title}</h1>
      <p className="text-14 text-8C8C80 mb-0">{t.subtitle}</p>

      <div className="create-roles-tabs" role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            id={`create-roles-tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`create-roles-panel-${tab.id}`}
            className={`create-roles-tab-btn ${
              activeTab === tab.id ? "active" : ""
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        id="create-roles-panel-roles"
        className="create-roles-tab-panel"
        role="tabpanel"
        aria-labelledby="create-roles-tab-roles"
        hidden={activeTab !== "roles"}
      >
        <div className="create-roles-toolbar">
          <Button className="create-roles-action-btn" onClick={openCreateRole}>
            {t.btn_create_role}
          </Button>
        </div>
        <div className="create-roles-table-container responsive-table-wrapper">
          <ConfigProvider direction={lang === "ar" ? "rtl" : "ltr"}>
            <Table
              className="create-roles-table"
              columns={columns}
              dataSource={roles}
              rowKey="id"
              loading={rolesLoading}
              pagination={rolesTablePagination}
              locale={{ emptyText: t.empty_no_roles }}
            />
          </ConfigProvider>
        </div>
      </div>

      <div
        id="create-roles-panel-users"
        className="create-roles-tab-panel"
        role="tabpanel"
        aria-labelledby="create-roles-tab-users"
        hidden={activeTab !== "users"}
      >
        <div className="create-roles-toolbar">
          <Button
            className="create-roles-action-btn"
            onClick={openUserModalForCreate}
          >
            {t.btn_create_user}
          </Button>
        </div>
        <div className="create-roles-table-container responsive-table-wrapper">
          <ConfigProvider direction={lang === "ar" ? "rtl" : "ltr"}>
            <Table
              className="create-roles-table"
              columns={userColumns}
              dataSource={assignUsers}
              rowKey="id"
              loading={usersLoading}
              pagination={usersTablePagination}
              locale={{ emptyText: t.empty_no_users }}
            />
          </ConfigProvider>
        </div>
      </div>

      <CreateRoleModal
        open={roleModalOpen}
        onClose={closeRoleModal}
        onSave={handleSaveRole}
        editingRole={editingRole}
        permissionRows={modalPermissionRows}
        moduleAccessTypes={modalModuleAccessTypes}
        modulesLoading={modalModulesLoading}
        modulesLoadError={modalModulesLoadError}
      />

      <CreateUserModal
        open={userModalOpen}
        onClose={closeUserModal}
        onSave={handleSaveUser}
        roleOptions={roleSelectOptions}
        editingUser={editingUser}
        initialRoleId={userModalInitialRoleId}
      />

      <RevokeUserConfirmModal
        open={Boolean(revokeTarget)}
        onClose={closeRevokeModal}
        onConfirm={confirmRevokeUser}
        loading={Boolean(revokingUserId)}
        mode={revokeModalMode}
      />
    </div>
  );
};

export default CreateRoles;
